import { useMemo } from 'react'

import { MultiSelect, Select, SimpleGrid } from '@mantine/core'
import { useGetList, useTranslate } from 'react-admin'
import { BeneficiaryApiDTO } from '../../../codegen/generated/Api.schemas'
import { useExportsContext, useExportsDispatchContext } from '../../../contexts/ExportsContext'
import { DateHelper } from '../../../helpers/Date.helper'
import { ProductsHelper } from '../../../helpers/Products.helper'
import { UsersHelper } from '../../../helpers/Users.helper'
import { ProductType } from '../../../types/ProductType'
import { YoutubeChannel } from '../../../types/YoutubeChannel'

export const SCAMExportsFilters = () => {
  const t = useTranslate()
  const context = useExportsContext()
  const { setSCAMFilters } = useExportsDispatchContext()

  const beneficiariesQuery = useGetList<BeneficiaryApiDTO>('beneficiaries', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
    filter: {
      ...((context.scam.filters.cms ?? [])?.length > 0
        ? { cms: context.scam.filters.cms }
        : undefined),
    },
  })
  const beneficiaries = useMemo(
    () =>
      (beneficiariesQuery.data ?? []).map(b => ({
        value: b.id,
        label: b.name,
      })),
    [beneficiariesQuery.data],
  )

  const productTypesQuery = useGetList<ProductType>('productTypes', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  })
  const productTypes = useMemo(
    () =>
      ProductsHelper.formatProductTypes(productTypesQuery.data ?? [], t).map(b => ({
        value: b.id,
        label: b.name,
      })),
    [productTypesQuery.data, t],
  )

  const youtubeChannelsQuery = useGetList<YoutubeChannel>('youtubeChannels', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  })
  const youtubeChannels = useMemo(
    () =>
      (youtubeChannelsQuery.data ?? []).map(b => ({
        value: b.id,
        label: b.name,
      })),
    [youtubeChannelsQuery.data],
  )

  const years = DateHelper.getYears().map(y => y.toString())

  return (
    <SimpleGrid cols={3} my="md">
      <MultiSelect
        label="CMS"
        data={UsersHelper.CMS}
        value={context.scam.filters.cms}
        onChange={id =>
          setSCAMFilters(f => ({
            ...f,
            cms: id ?? undefined,
          }))
        }
        searchable
        clearable
        autoComplete="off"
        data-form-type="other"
      />
      <MultiSelect
        label={t('pages.beneficiaries._title')}
        data={beneficiaries}
        value={context.scam.filters.beneficiaryIds}
        onChange={id =>
          setSCAMFilters(f => ({
            ...f,
            beneficiaryIds: id ?? undefined,
          }))
        }
        searchable
        clearable
        autoComplete="off"
        data-form-type="other"
      />
      <MultiSelect
        label={t('_generics.productTypes')}
        data={productTypes}
        value={context.scam.filters.productTypeIds}
        onChange={id =>
          setSCAMFilters(f => ({
            ...f,
            productTypeIds: id ?? undefined,
          }))
        }
        searchable
        clearable
        autoComplete="off"
        data-form-type="other"
      />
      <MultiSelect
        label={t('pages.youtubeChannels._title')}
        data={youtubeChannels}
        value={context.scam.filters.youtubeChannelIds}
        onChange={id =>
          setSCAMFilters(f => ({
            ...f,
            youtubeChannelIds: id ?? undefined,
          }))
        }
        searchable
        clearable
        autoComplete="off"
        data-form-type="other"
      />
      <Select
        label={t('_generics.productionYear')}
        data={years}
        value={(context.scam.filters.productionYears ?? [])[0]?.toString()}
        onChange={id =>
          setSCAMFilters(f => ({
            ...f,
            productionYears: id ? [parseInt(id)] : undefined,
          }))
        }
        searchable
        clearable
        autoComplete="off"
        data-form-type="other"
      />
    </SimpleGrid>
  )
}
